import { useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router";

import { PATHS } from "@/routes/paths";

import { FeatureIslandContainer } from "@/components/features/_layouts";
import * as MutationRepository from "@/components/features/settings/payment-credit/repository/mutation";
import type * as QueryRepository from "@/components/features/settings/payment-credit/repository/query";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import * as UseCase from "../../model/use-case";
import { ReceiptSettingInputZodSchema } from "../../model/validation-schema";
import { SettingsPaymentCreditHeader, SettingsPaymentCreditMain } from "../_components";
import { useRegistrationPayjp, useUpdatePaymentCredit } from "../_hooks";

type Props = {
	hotelID: string;
	serverModel: QueryRepository.ServerModel;
};

export const SettingsPaymentCreditPresenter = ({ hotelID, serverModel }: Props) => {
	const navigate = useNavigate();
	const { queryData, viewModel, onSetViewModel } = UseCase.useInitialized({ hotelID, serverModel });

	const {
		onSetTargetElement,
		onChangeHasPayjpValidation,
		onCreatePayjpToken,
		onChangeCardHolderName,
		onChangeCardEmail,
	} = useRegistrationPayjp(viewModel, onSetViewModel);

	const [canEdit, setCanEdit] = useState(false);
	const onChangeCanEdit = useCallback((value: boolean) => {
		setCanEdit(value);
	}, []);

	const onSuccessCallbackForOnUpdate = useCallback(
		() => navigate(generatePath(PATHS.HOTEL.TOP, { hotelID })),
		[hotelID, navigate],
	);
	const onUpdatePaymentCredit = useUpdatePaymentCredit(hotelID, onCreatePayjpToken, onSuccessCallbackForOnUpdate);

	useEffect(() => {
		onChangeCardEmail(queryData.hotelEmail);
	}, [queryData.hotelEmail, hotelID, navigate, onChangeCardEmail]);

	// 宛名の状態管理
	const [recipientAddressee, setRecipientAddressee] = useState(queryData.receipt.addressee);
	const [recipientAddresseeErrorMessage, setRecipientAddresseeErrorMessage] = useState("");

	const handleChangeRecipientAddressee = useCallback((value: string) => {
		setRecipientAddressee(value);

		// バリデーション実行
		const result = ReceiptSettingInputZodSchema.shape.addressee.safeParse(value);
		if (!result.success) {
			setRecipientAddresseeErrorMessage(result.error.errors[0].message);
		} else {
			setRecipientAddresseeErrorMessage("");
		}
	}, []);

	// 宛名が変更されたかどうか
	const isRecipientAddresseeChanged = useMemo(
		() => recipientAddressee !== queryData.receipt.addressee,
		[recipientAddressee, queryData.receipt.addressee],
	);

	// バリデーション（zodスキーマを使用）
	const isRecipientAddresseeValid = useMemo(
		() => ReceiptSettingInputZodSchema.shape.addressee.safeParse(recipientAddressee).success,
		[recipientAddressee],
	);

	// 保存ボタンの有効/無効判定
	// - 宛名が変更された場合、かつバリデーションが通っている場合は有効
	// - クレジットカード編集中でバリデーションが通っている場合は有効
	const isValid =
		(isRecipientAddresseeChanged && isRecipientAddresseeValid) || (canEdit && !viewModel.hasPayjpValidation);

	// 宛名の更新処理
	const { onUpdateReceipt } = MutationRepository.useReceiptSettingMutation();

	// ローディング表示
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	// 保存ボタンの処理（変更されたもののみmutationを実行）
	const handleUpdate = useCallback(async () => {
		// クレジットカード編集中の場合
		if (canEdit) {
			// 宛名が変更されている場合のみ、宛名の更新を実行
			if (isRecipientAddresseeChanged) {
				await onUpdateReceipt({
					id: queryData.receipt.id,
					hotelID,
					addressee: recipientAddressee,
				});
			}
			// クレジットカード情報を更新（ローディングはuseUpdatePaymentCredit内で処理）
			await onUpdatePaymentCredit();
			return;
		}

		// 宛名のみ更新の場合
		if (isRecipientAddresseeChanged) {
			showLoadingScreenCallback();
			await onUpdateReceipt({
				id: queryData.receipt.id,
				hotelID,
				addressee: recipientAddressee,
			}).finally(hideLoadingScreenCallback);

			notifyUserEventInfo({
				title: "宛名を更新しました",
			});
		}
	}, [
		canEdit,
		hideLoadingScreenCallback,
		hotelID,
		isRecipientAddresseeChanged,
		onUpdateReceipt,
		onUpdatePaymentCredit,
		queryData.receipt.id,
		recipientAddressee,
		showLoadingScreenCallback,
	]);

	return (
		<FeatureIslandContainer>
			<SettingsPaymentCreditHeader isValid={isValid} onUpdate={handleUpdate} />
			<SettingsPaymentCreditMain
				hotelID={hotelID}
				expired={queryData.expired}
				last4={queryData.last4}
				canEdit={canEdit}
				onChangeCanEdit={onChangeCanEdit}
				onSetTargetElement={onSetTargetElement}
				onChangeHasPayjpValidation={onChangeHasPayjpValidation}
				onChangeCardHolderName={onChangeCardHolderName}
				recipientAddressee={recipientAddressee}
				recipientAddresseeErrorMessage={recipientAddresseeErrorMessage}
				onChangeRecipientAddressee={handleChangeRecipientAddressee}
			/>
		</FeatureIslandContainer>
	);
};

SettingsPaymentCreditPresenter.displayName = "SettingsPaymentCreditPresenter";
